import { FC } from "react";
import { ChevronDown } from "lucide-react";
import { Checkbox } from "@/components/ui/checkbox";
import { Popover, PopoverTrigger, PopoverContent } from "@/components/ui/popover";
import { Separator } from "@/components/ui/separator";
import { cn } from "@/lib/utils";
import { AuditLogType, EventTypeGroup } from "../../models/AuditLog";

interface EventTypeGroupChipProps {
  group: EventTypeGroup;
  selectedEventTypes: Set<AuditLogType>;
  onToggleGroup: (groupTypes: AuditLogType[]) => void;
  onToggleType: (type: AuditLogType) => void;
}

const EventTypeGroupChip: FC<EventTypeGroupChipProps> = ({
  group,
  selectedEventTypes,
  onToggleGroup,
  onToggleType,
}) => {
  const groupKeys = group.types.map((t) => t.key);
  const selectedCount = groupKeys.filter((k) => selectedEventTypes.has(k)).length;
  const total = groupKeys.length;
  const state: "all" | "partial" | "none" =
    selectedCount === total ? "all" : selectedCount > 0 ? "partial" : "none";

  const label =
    state === "partial" ? `${group.label} (${selectedCount}/${total})` : group.label;

  return (
    <Popover>
      <div
        className={cn(
          "inline-flex items-center rounded-md border text-sm transition-colors",
          state === "all" && "bg-primary/10 border-primary/30 text-primary",
          state === "partial" && "bg-amber-50 border-amber-300/50 text-amber-800 dark:bg-amber-900/20 dark:border-amber-500/30 dark:text-amber-300",
          state === "none" && "bg-muted/50 border-border text-muted-foreground",
        )}
      >
        <button
          type="button"
          className={cn(
            "px-2.5 py-1 text-sm font-medium select-none rounded-l-md transition-colors",
            state === "all" && "hover:bg-primary/20",
            state === "partial" && "hover:bg-amber-100 dark:hover:bg-amber-900/40",
            state === "none" && "hover:bg-muted",
          )}
          onClick={() => onToggleGroup(groupKeys)}
        >
          {label}
        </button>
        <PopoverTrigger asChild>
          <button
            type="button"
            className={cn(
              "px-1 py-1 border-l rounded-r-md transition-colors",
              state === "all" && "border-primary/20 hover:bg-primary/20",
              state === "partial" && "border-amber-300/30 hover:bg-amber-100 dark:border-amber-500/20 dark:hover:bg-amber-900/40",
              state === "none" && "border-border hover:bg-muted",
            )}
            aria-label={`Filter ${group.label} event types`}
          >
            <ChevronDown className="size-3.5" />
          </button>
        </PopoverTrigger>
      </div>
      <PopoverContent className="w-56 p-1" align="start">
        <div className="flex items-center justify-between px-2 py-1.5">
          <span className="text-xs font-semibold text-muted-foreground">
            {group.label}
          </span>
          <button
            type="button"
            className="text-xs text-muted-foreground hover:text-foreground underline"
            onClick={() => onToggleGroup(groupKeys)}
          >
            {state === "all" ? "Deselect all" : "Select all"}
          </button>
        </div>
        <Separator />
        <div className="max-h-[300px] overflow-y-auto py-1">
          {group.types.map((t) => (
            <button
              key={t.key}
              type="button"
              className="flex w-full cursor-default items-center gap-2 rounded-sm px-2 py-1.5 text-sm outline-hidden select-none hover:bg-accent hover:text-accent-foreground"
              onClick={() => onToggleType(t.key)}
            >
              <Checkbox
                checked={selectedEventTypes.has(t.key)}
                tabIndex={-1}
                className="pointer-events-none"
              />
              <span>{t.label}</span>
            </button>
          ))}
        </div>
      </PopoverContent>
    </Popover>
  );
};

export default EventTypeGroupChip;
